/**
 * One row of the version-history list with an inline diff viewer.
 * Pure presentational — caller passes the row + current/published field
 * snapshots used to resolve Persian labels.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Archive } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { supabase } from '@/lib/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import type {
  FieldDefinition,
  FormVersionRow,
} from '../api/form-builder-api'

const CLASSIFICATION_LABELS: Record<
  string,
  { label: string; tone: 'safe' | 'breaking' | 'cosmetic' }
> = {
  safe:                          { label: 'ایمن',           tone: 'safe' },
  cosmetic_runtime:              { label: 'ظاهری',          tone: 'cosmetic' },
  breaking:                      { label: 'شکننده',         tone: 'breaking' },
  breaking_pending_data_check:   { label: 'شکننده (شرطی)',  tone: 'breaking' },
}

const CHANGE_TYPE_LABELS: Record<string, string> = {
  field_added:        'افزودن فیلد',
  field_removed:      'حذف فیلد',
  field_renamed:      'تغییر نام فیلد',
  field_label_changed: 'تغییر برچسب',
  field_description_changed: 'تغییر راهنما',
  field_type_changed: 'تغییر نوع فیلد',
  required_added:     'اجباری شد',
  required_removed:   'غیراجباری شد',
  option_added:       'افزودن گزینه',
  option_removed:     'حذف گزینه',
  option_renamed:     'تغییر برچسب گزینه',
  option_key_changed: 'تغییر کلید گزینه',
  validation_added:   'افزودن قانون اعتبارسنجی',
  validation_removed: 'حذف قانون اعتبارسنجی',
  validation_changed: 'تغییر قانون اعتبارسنجی',
  conditional_rule_added:   'افزودن قانون شرطی',
  conditional_rule_removed: 'حذف قانون شرطی',
  conditional_rule_changed: 'تغییر قانون شرطی',
  reorder:           'تغییر ترتیب',
  type_config_changed: 'تغییر تنظیمات نمایش',
}

interface VersionHistoryRowProps {
  row: FormVersionRow
  currentFields: FieldDefinition[]
  publishedFields?: FieldDefinition[]
}

export function VersionHistoryRow({
  row, currentFields, publishedFields,
}: VersionHistoryRowProps) {
  const [expanded, setExpanded] = useState(false)
  const qc = useQueryClient()
  const { toast } = useToast()
  const { role } = useProjectRole()
  const canManage = role === 'admin'

  const deprecateMutation = useMutation({
    mutationFn: async () => {
      const { setFormVersionDeprecationManual } = await import('../api/form-builder-api')
      return setFormVersionDeprecationManual(row.id, !row.is_deprecated_manual)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-version-history'] })
      qc.invalidateQueries({ queryKey: ['form-versions'] })
      toast({
        title: row.is_deprecated_manual ? 'نشانه‌گذاری منسوخ برداشته شد' : 'نسخه منسوخ شد',
        duration: 2000,
      })
    },
    onError: (err: Error) =>
      toast({ title: 'خطا', description: err.message, variant: 'destructive' }),
  })
  // change_summary is `jsonb` server-side. Most rows store an array; some
  // legacy rows store an object or string. Coerce to array defensively so
  // the page never crashes on malformed data.
  const summary: Array<{
    diffItemId: string
    classification: string
    type: string
    field_key: string | null
  }> = Array.isArray(row.change_summary)
    ? row.change_summary
    : row.change_summary && typeof row.change_summary === 'object'
      ? (Array.isArray((row.change_summary as { items?: unknown }).items)
          ? (row.change_summary as { items: typeof row.change_summary }).items as never
          : [])
      : []

  const safeCount = summary.filter(
    (c) => c.classification === 'safe' || c.classification === 'cosmetic_runtime',
  ).length
  const breakingCount = summary.filter(
    (c) => c.classification === 'breaking' || c.classification === 'breaking_pending_data_check',
  ).length
  const isMajor = row.bump_type === 'major'
  const totalChanges = summary.length

  const labelFor = (key: string | null) => {
    if (!key) return null
    const f = currentFields.find((x) => x.field_key === key)
      ?? publishedFields?.find((x) => x.field_key === key)
    return f?.label.fa || key
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => totalChanges > 0 && setExpanded((v) => !v)}
        disabled={totalChanges === 0}
        className={cn(
          'w-full p-4 space-y-2 text-start transition-colors',
          totalChanges > 0 && 'hover:bg-muted/30 cursor-pointer',
          totalChanges === 0 && 'cursor-default',
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold text-foreground">
              v{row.version_major}.{row.version_minor}
            </span>
            <Badge
              variant="outline"
              className={cn(
                'text-[10px]',
                isMajor
                  ? 'border-destructive/40 text-destructive'
                  : 'border-emerald-500/40 text-emerald-400',
              )}
            >
              {isMajor ? 'major' : 'minor'}
            </Badge>
            {row.is_current && (
              <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                نسخه فعلی
              </Badge>
            )}
            {row.is_deprecated_effective && !row.is_current && (
              <Badge
                variant="outline"
                className="text-[10px] border-yellow-500/40 text-yellow-400 gap-0.5"
                title={
                  row.is_deprecated_manual
                    ? 'این نسخه به‌صورت دستی منسوخ شده است'
                    : 'نسخه بعدی این فرم تغییر شکننده داشته — به‌صورت خودکار منسوخ شده'
                }
              >
                <Archive className="w-2.5 h-2.5" />
                منسوخ
              </Badge>
            )}
            {totalChanges > 0 && (
              expanded
                ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </div>
          <div className="text-[10px] text-muted-foreground text-end">
            <div>{new Date(row.published_at).toLocaleDateString('fa-IR')}</div>
            <div>{row.published_by_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          {safeCount > 0 && <span className="text-emerald-400">{safeCount} تغییر ایمن</span>}
          {breakingCount > 0 && (
            <span className="text-destructive">{breakingCount} تغییر شکننده</span>
          )}
          <span>{row.submission_count.toLocaleString('fa-IR')} سابقه روی این نسخه</span>
        </div>
      </button>

      {canManage && !row.is_current && (
        <div className="border-t border-border px-4 py-2 flex items-center justify-between gap-2 bg-muted/10">
          <span className="text-[10px] text-muted-foreground">
            {row.is_deprecated_manual
              ? 'این نسخه را به‌صورت دستی منسوخ کرده‌اید'
              : row.is_deprecated_effective
              ? 'به دلیل تغییر شکننده در نسخه بعدی، خودکار منسوخ است'
              : 'این نسخه فعال است و قابل ارسال نهایی'}
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 text-[10px] gap-1"
            disabled={deprecateMutation.isPending}
            onClick={(e) => { e.stopPropagation(); deprecateMutation.mutate() }}
            title={row.is_deprecated_manual
              ? 'لغو نشانه‌گذاری منسوخ'
              : 'منسوخ کردن این نسخه — کاربران را مجبور به ارتقا می‌کند'}
          >
            <Archive className="w-3 h-3" />
            {row.is_deprecated_manual ? 'لغو منسوخ' : 'منسوخ کن'}
          </Button>
        </div>
      )}

      {expanded && summary.length > 0 && (
        <div className="border-t border-border bg-background/40 p-3 space-y-1.5">
          {summary.map((c, idx) => {
            const cls = CLASSIFICATION_LABELS[c.classification]
              ?? { label: c.classification, tone: 'safe' as const }
            const fieldLabel = labelFor(c.field_key)
            const typeLabel = CHANGE_TYPE_LABELS[c.type] ?? c.type
            return (
              <div
                key={c.diffItemId ?? idx}
                className="flex items-start gap-2 text-xs px-2 py-1.5 rounded-md border border-border/50 bg-card/60"
              >
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[9px] shrink-0',
                    cls.tone === 'breaking' && 'border-destructive/40 text-destructive',
                    cls.tone === 'safe' && 'border-emerald-500/40 text-emerald-400',
                    cls.tone === 'cosmetic' && 'border-sky-500/40 text-sky-400',
                  )}
                >
                  {cls.label}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-foreground">{typeLabel}</div>
                  {fieldLabel && (
                    <div className="text-[10px] text-muted-foreground truncate">
                      فیلد: {fieldLabel}
                      {c.field_key && c.field_key !== fieldLabel && (
                        <span className="font-mono opacity-60 ms-1" dir="ltr">({c.field_key})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
